import { useState } from 'react'

import { useTranslation } from 'react-i18next'

import { TertiaryButton, Text } from '@enechain/ecloud-designsystem'

import { Modal } from '~/components/common/Modal'
import { DEFAULT_STACK_ORDER_ITEMS } from '~/constants/generationSource'
import { StackOrderList } from '~/pages/BalanceGeneration/components/StackOrderList'
import {
  type UsePersonalSettingsReturn,
  usePersonalSettings,
} from '~/pages/Settings/hooks/usePersonalSettings'

/**
 * 同時同量 (発電) 画面から「積み上げ順・色」を編集する導線 (ボタン + モーダル)。
 *
 * design `demand`/`balance/hatsu.html` が #7059 で置いたのと同じ形。 8080 側の
 * コメント:「積み上げ順と色は**チャートを見ながらでないと良し悪しが判断できない**
 * ため、 調整と結果を同じ画面に収める」。 積み上げ順・色の編集場所はこの画面のみとする。
 *
 * - 中身は既存の `StackOrderList`。 値は利用者単位で保存し、画面間で引き継ぐ。
 * - 保存は `usePersonalSettings` が debounce して即時反映する (Save ボタンは
 *   持たない)。 チャートは
 *   `useGenerationSourceList` が同じ query key を購読しているので、
 *   **モーダルを開いたまま背後のチャートが変わる**。
 * - したがって footer は持たず、閉じるのは × / Esc / オーバーレイに任せる
 *   (`Modal` が 3 経路とも `onClose` に集約する)。
 */
export const StackOrderModalButton = (): React.ReactElement => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const personalSettings = usePersonalSettings({
    enabled: isOpen,
    throwOnError: false,
    saveErrorToastTitle: t('page:balanceGeneration.stackOrder.saveError'),
  })

  return (
    <>
      {/* TertiaryButton (デザイナー指摘 2026-10-02)。 8080 の `#btn-stack-order` も
          `btn btn-tertiary btn-sm` (= 高さ 32px で DS の size="sm" と同じ) */}
      <TertiaryButton onClick={() => setIsOpen(true)} size="sm">
        {t('page:balanceGeneration.stackOrder.open')}
      </TertiaryButton>
      <Modal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title={t('page:balanceGeneration.stackOrder.title')}
      >
        {/* hook はモーダル外で保ち、閉じた後の保存失敗も通知できるようにする。
            fetch は open 時だけ実行し、失敗は既定表示へフォールバックする。 */}
        {isOpen ? <StackOrderEditor {...personalSettings} /> : null}
      </Modal>
    </>
  )
}

/** モーダル表示中だけ mount するエディタ。設定 hook は親で維持する。 */
const StackOrderEditor = (
  settings: Pick<
    UsePersonalSettingsReturn,
    | 'settings'
    | 'moveStackOrderUp'
    | 'moveStackOrderDown'
    | 'resetStackOrder'
    | 'updateStackOrderColor'
  >,
): React.ReactElement => {
  const { t } = useTranslation()

  return (
    <>
      {/* 文字は md / `Text.highEmphasis` (デザイナー指定 2026-09-29)。
          8080 の `.st-hint` は md / `--text-mid-emphasis` だが、モーダルでは
          この 1 文が操作の前提を説明する唯一の文なので本文色で出す [design 差異]。 */}
      <Text marginBottom={3} color="Text.highEmphasis" fontSize="md">
        {t('page:balanceGeneration.stackOrder.hint')}
      </Text>
      {/* 器の幅に合わせる。既定の 480px だと右側に余白が空き、ヘッダ行
          (案内文 + リセット) の右端と揃わない (デザイナー指摘 2026-09-29)。 */}
      <StackOrderList
        maxWidth="100%"
        onColorChange={settings.updateStackOrderColor}
        onMoveDown={settings.moveStackOrderDown}
        onMoveUp={settings.moveStackOrderUp}
        onResetDefault={settings.resetStackOrder}
        value={settings.settings?.stackOrder ?? DEFAULT_STACK_ORDER_ITEMS}
      />
    </>
  )
}
